/**
 * 포스트 표(`PostTable`)의 열 목록과 사람별 배치 — 어떤 열이 기본으로 어떤 차례로 서고, 사용자가 숨기거나 차례를 바꾼 배치를 어떻게 맞추나.
 *
 * 🔑 열 id 하나 = 「열 설정」 한 줄. 이어진 열(1·3·7·30일 넷 · 반응 다섯)은 id 하나로 함께 움직이고 함께 숨는다.
 *   반응을 지표마다 나누는 표(`splitReactionColumns`)는 지표마다 한 줄(`reaction:like` …)이다.
 * 🔑 배치를 안 주면 표는 기본 차례(`postTableColumnCatalog`) 그대로다.
 * 🔑 배치에 없는 새 열은 기본 자리에 끼고, 표에 없는 열은 조용히 버린다(`resolvePostTableColumns`).
 * ⚠️ 계정·제목/내용(`POST_TABLE_LOCKED_COLUMNS`)은 숨길 수 없다 — 줄을 읽는 데 필요하다. 옮기기만 된다.
 * 🔑 1·3·7·30일 열과 `defaultHidden` 을 단 덧붙인 열은 처음엔 꺼진 열이다 — 배치가 그 열을 모르면 숨은 채로 선다.
 * 🔑 덧붙인 열에 `group` 을 달면 「열 설정」에서 따로 묶여 서고, 표에서는 제 `at` 자리에 붙어 선다.
 *   지표마다 따로 세우는 표(`viewDayColumnsPerMetric`)는 지표마다 한 줄(`viewDays:{지표}` — 「조회수 일차별」 …)로 반응 열 뒤(메모 앞)에 서고,
 *   아니면 날짜 뒤 한 줄(`viewDays`)이다. 어느 쪽이든 워크스페이스가 숨긴 반응 지표는 빠진다.
 */

import { dayMetricOptionsFor } from 'src/utils/metrics/metric-day-detail';

/** 덧붙인 열이 서는 자리 — `afterReactions` 는 반응 열 바로 뒤(일차별 열 앞) · `beforeMemo` 는 반응·일차별 열 뒤 · 메모 앞 */
export type PostTableColumnAt = 'afterChannel' | 'afterDate' | 'afterView' | 'afterReactions' | 'beforeMemo' | 'end';

/** 사람별 배치 — `order` 는 열 id 차례, `hidden` 은 숨긴 열 id */
export type PostTableColumnLayout = { order: readonly string[]; hidden: readonly string[] };

/** 「열 설정」 한 줄 */
export type PostTableColumnMeta = {
  id: string;
  label: string;
  /** 이름 옆 작은 글씨(이어진 열의 구성) */
  hint?: string;
  /** 숨길 수 없는 열 */
  locked: boolean;
  /** 처음엔 꺼진 열 — 배치가 이 열을 모르면 숨긴다 */
  defaultHidden?: boolean;
  /** 「열 설정」에서 따로 묶여 서는 묶음 열쇠 — 그 묶음 안에서만 옮긴다(없으면 기본 목록) */
  group?: string;
};

/** 「열 설정」 한 줄과 지금 보이는지 */
export type PostTableColumnItem = PostTableColumnMeta & { visible: boolean };

/** 반응 지표 열 다섯 — 표의 머리·칸·합계 줄이 같은 목록을 본다 */
export const POST_TABLE_METRIC_COLUMNS = [
  { field: 'like', label: '좋아요', icon: '/assets/icons/icon_svg/like.svg' },
  { field: 'comment', label: '댓글', icon: '/assets/icons/icon_svg/reply.svg' },
  { field: 'saved', label: '저장', icon: '/assets/icons/icon_svg/bookmark.svg' },
  { field: 'share', label: '공유', icon: '/assets/icons/icon_svg/share.svg' },
  { field: 'download', label: '다운로드', icon: '/assets/icons/icon_svg/download.svg' },
] as const;

/** 숨길 수 없는 열 — 계정 · 제목/내용 */
export const POST_TABLE_LOCKED_COLUMNS: ReadonlySet<string> = new Set(['account', 'content']);

/** 덧붙인 열(`extraColumns`)의 열 id */
export function postTableExtraColumnId(key: string): string {
  return `extra:${key}`;
}

/** 지표 하나의 1·3·7·30일 열 id(`viewDays:likeCount`) */
export function postTableDayColumnId(metricKey: string): string {
  return `${DAY_COLUMN_PREFIX}${metricKey}`;
}

/**
 * 1·3·7·30일 열 id 의 지표.
 * @returns 지표마다 선 열(`viewDays:likeCount`)이면 그 지표 · 한 줄짜리(`viewDays`)면 `''` · 일차 열이 아니면 `null`
 */
export function postTableDayColumnMetric(id: string): string | null {
  if (id === 'viewDays') return '';
  return id.startsWith(DAY_COLUMN_PREFIX) ? id.slice(DAY_COLUMN_PREFIX.length) : null;
}

/** 반응 지표 하나의 열 id(`reaction:like`) */
export function postTableReactionColumnId(field: string): string {
  return `${REACTION_COLUMN_PREFIX}${field}`;
}

/**
 * 반응 지표 한 칸짜리 열 id 의 지표.
 * @returns `like` · `comment` … · 그런 열이 아니면 `null`
 */
export function postTableReactionColumnField(id: string): string | null {
  return id.startsWith(REACTION_COLUMN_PREFIX) ? id.slice(REACTION_COLUMN_PREFIX.length) : null;
}

/** 기본 차례를 정하는 표의 설정 — `PostTable` 의 같은 이름 프롭 */
export type PostTableCatalogInput = {
  hideChannel?: boolean;
  hideCountry?: boolean;
  enableViewDayColumns?: boolean;
  /** `enableViewDayColumns` 일 때 지표마다 한 줄(숨긴 반응 지표는 빠진다) */
  viewDayColumnsPerMetric?: boolean;
  /** 반응 지표마다 한 줄(숨긴 반응 지표는 빠진다) */
  splitReactionColumns?: boolean;
  hiddenMetricFields?: ReadonlySet<string>;
  enableMemoColumn?: boolean;
  enableVideoCheckColumn?: boolean;
  extraColumns?: readonly { key: string; label: string; menuLabel?: string; at?: PostTableColumnAt; defaultHidden?: boolean; group?: string }[];
  extraColumnsAt?: PostTableColumnAt;
  dateLabel?: string;
};

/**
 * 표에 서는 열을 기본 차례로 — `#` 칸은 늘 맨 앞이라 목록에 없다.
 * @returns 열 id · 이름 · 잠김
 */
export function postTableColumnCatalog(input: PostTableCatalogInput = {}): PostTableColumnMeta[] {
  const {
    hideChannel = false,
    hideCountry = false,
    enableViewDayColumns = false,
    viewDayColumnsPerMetric = false,
    splitReactionColumns = false,
    hiddenMetricFields,
    enableMemoColumn = false,
    enableVideoCheckColumn = false,
    extraColumns = [],
    extraColumnsAt = 'end',
    dateLabel = '날짜',
  } = input;
  const col = (id: string, label: string, hint?: string): PostTableColumnMeta => ({
    id,
    label,
    ...(hint ? { hint } : {}),
    locked: POST_TABLE_LOCKED_COLUMNS.has(id),
  });
  const extras = (at: PostTableColumnAt) => extraColumns
    .filter((c) => (c.at ?? extraColumnsAt) === at)
    .map((c) => ({
      ...col(postTableExtraColumnId(c.key), c.menuLabel || c.label),
      ...(c.defaultHidden ? { defaultHidden: true } : {}),
      ...(c.group ? { group: c.group } : {}),
    }));
  const shownMetrics = POST_TABLE_METRIC_COLUMNS.filter(({ field }) => !hiddenMetricFields?.has(field));
  const metricLabels = shownMetrics.map(({ label }) => label);
  const reactionColumns = splitReactionColumns
    ? shownMetrics.map(({ field, label }) => col(postTableReactionColumnId(field), label))
    : metricLabels.length > 0 ? [col('reactions', '반응', reactionHint(metricLabels))] : [];
  const perMetricDays = enableViewDayColumns && viewDayColumnsPerMetric
    ? dayMetricOptionsFor(hiddenMetricFields).map(({ value, label }) => ({ ...col(postTableDayColumnId(value), `${label} 일차별`, DAY_HINT), defaultHidden: true }))
    : [];

  return [
    ...(hideChannel ? [] : [col('channel', '채널')]),
    ...extras('afterChannel'),
    col('account', '계정'),
    col('image', '이미지'),
    col('content', '제목/내용'),
    ...(hideCountry ? [] : [col('country', '국가')]),
    col('date', dateLabel),
    ...extras('afterDate'),
    ...(enableViewDayColumns && !viewDayColumnsPerMetric ? [{ ...col('viewDays', '일차별 추이', DAY_HINT), defaultHidden: true }] : []),
    col('view', '조회수'),
    ...extras('afterView'),
    col('er', 'E/R'),
    ...reactionColumns,
    ...extras('afterReactions'),
    ...perMetricDays,
    ...extras('beforeMemo'),
    ...(enableMemoColumn ? [col('memo', '메모')] : []),
    ...(enableVideoCheckColumn ? [col('videoCheck', '영상 평가')] : []),
    ...extras('end'),
  ];
}

/**
 * `primary` 차례를 지키고, `reference` 에만 있는 id 는 `reference` 에서 바로 앞에 있던 것 뒤에 끼운다(앞이 없으면 맨 앞).
 * @returns 둘을 합친 차례(겹침 없음)
 */
export function mergeColumnOrder(primary: readonly string[], reference: readonly string[]): string[] {
  const out = [...new Set(primary)];
  reference.forEach((id, i) => {
    if (out.includes(id)) return;
    let at = 0;
    for (let j = i - 1; j >= 0; j -= 1) {
      const prev = out.indexOf(reference[j]);
      if (prev >= 0) {
        at = prev + 1;
        break;
      }
    }
    out.splice(at, 0, id);
  });
  return out;
}

/**
 * 지금 표의 열에 맞춘 배치 — 차례는 표의 열 전부, 숨김은 표에 있고 잠기지 않은 것만.
 * @param catalogIds 표의 열 id(기본 차례)
 * @param layout 저장된 배치 — 없으면 기본
 * @param defaultHidden 처음엔 꺼진 열 id — 배치의 차례에 없으면 숨긴다
 */
export function normalizePostTableColumnLayout(
  catalogIds: readonly string[],
  layout: PostTableColumnLayout | null | undefined,
  defaultHidden: readonly string[] = []
): { order: string[]; hidden: string[] } {
  const known = new Set(catalogIds);
  const storedOrder = (layout?.order ?? []).filter((id) => known.has(id));
  const order = mergeColumnOrder(storedOrder, catalogIds);
  const placed = new Set(storedOrder);
  const hiddenSet = new Set([
    ...(layout?.hidden ?? []),
    ...defaultHidden.filter((id) => !placed.has(id)),
  ].filter((id) => known.has(id) && !POST_TABLE_LOCKED_COLUMNS.has(id)));
  return { order, hidden: order.filter((id) => hiddenSet.has(id)) };
}

/**
 * 표에 실제로 펼 열 — 배치를 맞춘 차례에서 숨긴 것을 뺀다. 배치도 처음엔 꺼진 열도 없으면 기본 차례 그대로.
 * @param defaultHidden 처음엔 꺼진 열 id(`normalizePostTableColumnLayout` 과 같다)
 * @returns 보이는 열 id 차례
 */
export function resolvePostTableColumns(
  catalogIds: readonly string[],
  layout: PostTableColumnLayout | null | undefined,
  defaultHidden: readonly string[] = []
): string[] {
  if (!layout && defaultHidden.length === 0) return [...catalogIds];
  const { order, hidden } = normalizePostTableColumnLayout(catalogIds, layout, defaultHidden);
  const off = new Set(hidden);
  return order.filter((id) => !off.has(id));
}

/** 목록에서 처음엔 꺼진 열(`defaultHidden`)의 id */
export function postTableDefaultHiddenIds(catalog: readonly PostTableColumnMeta[]): string[] {
  return catalog.filter((c) => c.defaultHidden).map((c) => c.id);
}

/**
 * 저장할 배치 — 지금 표의 배치(`next`)에, 지금 표에 없는 열(꺼 둔 1·3·7·30일 같은 것)의 저장된 자리·숨김을 이어 붙인다.
 * @param prev 저장돼 있던 배치
 * @param next 지금 표의 열로 맞춘 새 배치
 * @param catalogIds 지금 표의 열 id
 */
export function mergeStoredColumnLayout(
  prev: PostTableColumnLayout | null | undefined,
  next: PostTableColumnLayout,
  catalogIds: readonly string[]
): PostTableColumnLayout {
  const known = new Set(catalogIds);
  const keptHidden = (prev?.hidden ?? []).filter((id) => !known.has(id));
  return {
    order: mergeColumnOrder(next.order, prev?.order ?? []),
    hidden: [...new Set([...next.hidden, ...keptHidden])],
  };
}

/**
 * 한 열을 다른 열 자리에 끼운다(끌어 놓기).
 * @returns 새 차례 — 둘 중 하나라도 없으면 그대로
 */
export function moveColumn(order: readonly string[], activeId: string, overId: string): string[] {
  const from = order.indexOf(activeId);
  const to = order.indexOf(overId);
  if (from < 0 || to < 0 || from === to) return [...order];
  const out = [...order];
  out.splice(from, 1);
  out.splice(to, 0, activeId);
  return out;
}

/**
 * 두 배치가 값으로 같은가 — 차례·숨김의 id 목록을 차례까지 견준다(`null` 은 `null` 하고만 같다).
 * @returns 같으면 `true`
 */
export function sameLayout(a: PostTableColumnLayout | null | undefined, b: PostTableColumnLayout | null | undefined): boolean {
  if (!a || !b) return (a ?? null) === (b ?? null);
  return sameIds(a.order, b.order) && sameIds(a.hidden, b.hidden);
}

/** 저장 배치 한 벌의 열 id 상한 — 넘으면 서버가 거절한다 */
export const POST_TABLE_LAYOUT_MAX_IDS = 200;
/** 열 id 하나의 글자 상한 — 넘으면 서버가 거절한다 */
export const POST_TABLE_LAYOUT_MAX_ID_LENGTH = 100;

/**
 * 저장된 배치 읽기 — 모양이 어긋나면 `null`, 글자가 아니거나 빈 id 는 버린다(브라우저 저장값 · 워크스페이스 문서 공용).
 * @returns `{ order, hidden }` 또는 `null`
 */
export function toPostTableColumnLayout(raw: unknown): PostTableColumnLayout | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const { order, hidden } = raw as { order?: unknown; hidden?: unknown };
  const ids = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x !== '') : []);
  return { order: ids(order), hidden: ids(hidden) };
}

/**
 * 저장할 배치 검사 — `order`·`hidden` 은 빈 칸 없는 글자 목록이고 상한(`POST_TABLE_LAYOUT_MAX_IDS` · `POST_TABLE_LAYOUT_MAX_ID_LENGTH`) 안이어야 한다.
 * 겹친 id 는 하나로 접는다.
 * @returns 맞으면 `{ ok: true, layout }` · 아니면 `{ ok: false, message }`
 */
export function validatePostTableColumnLayout(
  raw: unknown
): { ok: true; layout: { order: string[]; hidden: string[] } } | { ok: false; message: string } {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { ok: false, message: 'layout must be an object' };
  const { order, hidden } = raw as { order?: unknown; hidden?: unknown };
  const list = (v: unknown): string[] | null => {
    if (!Array.isArray(v) || v.length > POST_TABLE_LAYOUT_MAX_IDS) return null;
    const ok = v.every((x) => typeof x === 'string' && x.trim() !== '' && x.length <= POST_TABLE_LAYOUT_MAX_ID_LENGTH);
    return ok ? [...new Set(v as string[])] : null;
  };
  const o = list(order);
  const h = list(hidden);
  if (!o || !h) {
    return {
      ok: false,
      message: `order and hidden must be arrays of non-empty strings (≤${POST_TABLE_LAYOUT_MAX_IDS} ids, ≤${POST_TABLE_LAYOUT_MAX_ID_LENGTH} chars)`,
    };
  }
  return { ok: true, layout: { order: o, hidden: h } };
}

// ---- 이하 비공개 ----------------------------------------------------------

/** 반응 줄의 작은 글씨 — 셋 이상이면 앞 둘만 적고 개수를 붙인다(창 폭에 들게) */
function reactionHint(labels: readonly string[]): string {
  return labels.length > 2 ? `${labels[0]}·${labels[1]} 등 ${labels.length}개` : labels.join('·');
}

/** 지표마다 선 1·3·7·30일 열 id 의 앞말 */
const DAY_COLUMN_PREFIX = 'viewDays:';

/** 1·3·7·30일 줄의 작은 글씨 */
const DAY_HINT = '1·3·7·30일';

/** 반응 지표 한 칸짜리 열 id 의 앞말 */
const REACTION_COLUMN_PREFIX = 'reaction:';

function sameIds(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}
